import {
	Assessment as AssessmentIcon,
	Description as DescriptionIcon,
	Download as DownloadIcon,
	List as ListIcon,
	Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DeleteConfigurationDialog, ResampleSensorModal } from '@Components';
import { FLAGS, NamespacedStoreState } from '@Types';
import {
	LeftHeader,
	RightHeader,
	StyledActionBar,
	StyledHeaderButton,
	StyledHeaderText,
} from './SensorConfigurationsControlsBar.styles';
import React, { useEffect, useMemo, useState } from 'react';
import {
	SplitButton,
	StylovyzeSearchBar,
	useIsAdmin,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import {
	deleteMultipleConfigurations,
	getSensorsReadingsCount,
} from '@Actions/configurations';
import { getPhysicalSensorsCount, getVirtualSensorsCount } from '@Actions';
import {
	useConfigurationsPagination,
	useConfigurationsSearch,
	useSensorsRedirects,
} from '@Hooks';
import { useDispatch, useSelector } from 'react-redux';

import { DataSourceFilter } from '@Components/DataSourceFilter';
import { DeleteDataIcon } from '@Components/icons/DeleteDataIcon';
import { DeleteSensorsIcon } from '@Components/icons/DeleteSensorsIcon';
import FileUploadOutlineIcon from '@Components/icons/FileUploadOutlineIcon';
import { SensorTypesFilter } from '@Components/SensorTypesFilter';
import { SensorsReadingCountFileName } from '@Types/configurations.types';
import { SystemType } from '@Types/tiling.types';
import { getValue } from '@Selectors';
import { isNonDeletableConfiguration } from '@Utils';
import { queueJob } from '@Reducers/tiling.reducer';
import { selectIsQueueBlocked } from '@Selectors/tiling.selectors';
import { selectPhysicalSensor } from '@Selectors/physicalSensor.selector';
import { selectVirtualSensor } from '@Selectors/virtualSensor.selector';
import { useGlobalization } from '@Translations';

interface SplitButtonOption {
	title: string;
	icon?: React.ReactNode;
	onClick: () => void;
	cy?: string;
	key?: number;
	disabled?: boolean;
}

const SensorConfigurationsControlsBar = () => {
	const { t } = useGlobalization();
	const bulkSensorDeletionIsEnabled = useIsFeatureEnabled(
		FLAGS.BULK_SENSOR_DATA_DELETION,
	);
	const regenerateTilesEnabled = useIsFeatureEnabled(
		FLAGS.REGENERATE_TILES_SENSOR,
	);
	const isRegenerateTilesButtonDisabled = useSelector(
		(state: NamespacedStoreState) =>
			selectIsQueueBlocked(state, SystemType.Sensors),
	);

	const [
		redirectToCreateSensor,
		redirectToBulkSensorCreation,
		redirectToUploadSensorData,
	] = useSensorsRedirects();
	const {
		configurations: {
			physicalOrVirtual,
			status,
			checkOrUncheckedRows,
			headerCheckboxChecked,
		},
	} = useSelector(getValue);

	const [, fetchConfigurationsWithCriteria, fetchConfigurationsRfi] =
		useConfigurationsPagination();
	const {
		searchValue,
		changeSearchValue,
		changeSensorTypes,
		sensorTypes,
		dataSourceTypes,
		changeDataSourceTypes,
	} = useConfigurationsSearch();

	//For virtual sensors count
	const dispatch = useDispatch();
	const { virtualCount } = useSelector(selectVirtualSensor);
	const { physicalCount } = useSelector(selectPhysicalSensor);
	const isAdmin = useIsAdmin();

	useEffect(() => {
		dispatch(getVirtualSensorsCount());
		dispatch(getPhysicalSensorsCount());
	}, []);

	const selectedSensorIds = useMemo(() => {
		if (headerCheckboxChecked) return ['*'];
		return checkOrUncheckedRows.map(el => el.sensorId);
	}, [headerCheckboxChecked, checkOrUncheckedRows]);

	const [resampleModalOpen, setResampleModalOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [onlyData, setOnlyData] = useState(false);

	const handleOnClickNewSensorButton = (): void => {
		redirectToCreateSensor();
	};
	const handleOnClickBulkCreationButton = (): void => {
		redirectToBulkSensorCreation();
	};
	const handleOnClickUploadDataButton = (): void => {
		redirectToUploadSensorData();
	};
	const handleOnClickBulkResampleButton = (): void => {
		setResampleModalOpen(true);
	};
	const handleOnDownloadRFIOptionOptionButton = (): void => {
		fetchConfigurationsRfi(1000);
	};

	const closeResampleModalHandler = (): void => {
		setResampleModalOpen(false);
	};

	const handleDownloadSensorDataButton = (): void => {
		dispatch(
			getSensorsReadingsCount({ fileName: SensorsReadingCountFileName }),
		);
	};

	const handleSensorTypesChange = (sensorTypes: string[]) => {
		changeSensorTypes(sensorTypes);
		fetchConfigurationsWithCriteria(1, searchValue, sensorTypes);
	};
	const handleDataSourceTypesChange = (dataSourceTypes: string[]) => {
		changeDataSourceTypes(dataSourceTypes);
		fetchConfigurationsWithCriteria(
			1,
			searchValue,
			sensorTypes,
			dataSourceTypes,
		);
	};

	const actionOptions: SplitButtonOption[] = [
		{
			cy: 'newSensorButton',
			title: t('New Sensor'),
			icon: <ListIcon style={{ color: '#768D95' }} />,
			onClick: handleOnClickNewSensorButton,
		},
		{
			title: t('Bulk Sensor Creation'),
			icon: (
				<ListIcon
					style={{ color: '#768D95' }}
					data-cy="bulkSensorOption"
				/>
			),
			onClick: handleOnClickBulkCreationButton,
		},
		{
			cy: 'bulkResampleOption',
			title: t('Bulk Resample'),
			icon: (
				<AssessmentIcon
					style={{ color: '#768D95' }}
					data-cy="bulkResampleOption"
				/>
			),
			onClick: handleOnClickBulkResampleButton,
			disabled: false,
		},
		{
			cy: 'downloadRFIOption',
			title: t('Download RFI'),
			icon: (
				<DescriptionIcon
					style={{ color: '#768D95' }}
					data-cy="downloadRFIOption"
				/>
			),
			onClick: handleOnDownloadRFIOptionOptionButton,
		},
	];

	actionOptions.push({
		title: t('Upload Data'),
		icon: <FileUploadOutlineIcon data-cy="UploadSensorDataOption" />,
		onClick: handleOnClickUploadDataButton,
	});

	if (bulkSensorDeletionIsEnabled) {
		actionOptions.push({
			title: t('Delete Data'),
			icon: (
				<DeleteDataIcon
					style={{ marginLeft: '4px', marginRight: '7px' }}
					data-cy="DeleteDataOption"
				/>
			),
			onClick: () => {
				setOnlyData(true);
				setIsDeleteDialogOpen(true);
			},
			disabled: headerCheckboxChecked
				? false
				: checkOrUncheckedRows.filter(
						entry =>
							!isNonDeletableConfiguration(entry.dataSourceType),
				  ).length === 0,
		});
	}

	actionOptions.push({
		title: t('Download Sensors Count'),
		icon: (
			<DownloadIcon
				style={{ color: '#768D95' }}
				data-cy="downloadSensorsCount"
			/>
		),
		onClick: handleDownloadSensorDataButton,
	});

	if (bulkSensorDeletionIsEnabled) {
		actionOptions.push({
			title: t('Delete Sensor(s)'),
			icon: (
				<DeleteSensorsIcon
					style={{ marginLeft: '4px', marginRight: '7px' }}
					data-cy="deleteSensors"
				/>
			),
			onClick: () => {
				setOnlyData(false);
				setIsDeleteDialogOpen(true);
			},
			disabled: headerCheckboxChecked
				? false
				: checkOrUncheckedRows.filter(
						entry =>
							!isNonDeletableConfiguration(entry.dataSourceType),
				  ).length === 0,
		});
	}

	if (regenerateTilesEnabled) {
		actionOptions.push({
			title: t('Refresh Sensors On Map'),
			icon: (
				<RefreshIcon
					style={{ color: '#768D95' }}
					data-cy="generateSensorTiles"
				/>
			),
			onClick: () => {
				dispatch(
					queueJob({
						systemType: SystemType.Sensors,
					}),
				);
			},
			disabled: isRegenerateTilesButtonDisabled,
		});
	}

	const areFiltersEnabled = useIsFeatureEnabled(
		'info360-table-selector-and-types-filter',
	);

	const handleOnConfirm = () => {
		setIsDeleteDialogOpen(false);
		dispatch(
			deleteMultipleConfigurations({
				virtualOrPhysical: physicalOrVirtual,
				sensorTypes,
				checkOrUncheckedRows,
				headerCheckboxChecked,
				onlyData,
				searchValue,
				dataSourceTypes,
			}),
		);
	};

	return (
		<StyledActionBar>
			<LeftHeader>
				<StylovyzeSearchBar
					value={searchValue}
					placeholder={t('Search configuration')}
					handleSearchSubmit={(query: string) => {
						changeSearchValue(query);
						fetchConfigurationsWithCriteria(1, query, sensorTypes);
					}}
					dataCy={'configurationsSearch'}
					searchInProgress={status === 'pending'}
				/>
				{areFiltersEnabled ? (
					<>
						<SensorTypesFilter
							onSensorTypesChange={handleSensorTypesChange}
						/>
						<DataSourceFilter
							onDataSourceTypeChange={handleDataSourceTypesChange}
						/>
					</>
				) : null}
			</LeftHeader>

			<RightHeader>
				<StyledHeaderText>
					{t('Physical Sensors Used: {{total}} / ', {
						total: physicalCount ?? 0,
					})}
					{t('Virtual Sensors Used: {{total}}', {
						total: virtualCount ?? 0,
					})}
				</StyledHeaderText>
				{isAdmin && (
					<StyledHeaderButton>
						<SplitButton
							oneClickMenu={true}
							includeSelectedInMenu={false}
							options={actionOptions || null}
						/>
					</StyledHeaderButton>
				)}
			</RightHeader>
			<ResampleSensorModal
				open={resampleModalOpen}
				sensorId="*"
				sensorIds={selectedSensorIds}
				onClose={closeResampleModalHandler}
			/>
			<DeleteConfigurationDialog
				configurationsToDelete={
					headerCheckboxChecked
						? 'Multiple'
						: checkOrUncheckedRows.map(r => r.sensorId)
				}
				onCancel={() => setIsDeleteDialogOpen(false)}
				onConfirm={handleOnConfirm}
				open={isDeleteDialogOpen}
				onlyData={onlyData}
			/>
		</StyledActionBar>
	);
};

export default SensorConfigurationsControlsBar;
